import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const LandingPage = ({ onStaffLoginClick, onPatientLoginClick }) => {
  const [counts, setCounts] = useState({ years: 0, patients: 0, emergency: 0 });
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [bookedAppointments, setBookedAppointments] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [bookingData, setBookingData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    age: '',
    gender: '',
    bloodGroup: '',
    appointmentDate: '',
    appointmentTime: '',
    address: '',
    doctorId: ''
  });
  const statsRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    fetchDoctors();
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));

    const statsObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !hasAnimated) {
        setHasAnimated(true);
        animateCount('years', 15, 2000);
        animateCount('patients', 10000, 2000);
        animateCount('emergency', 24, 2000);
      }
    }, { threshold: 0.5 });

    if (statsRef.current) {
      statsObserver.observe(statsRef.current);
    }

    return () => {
      observer.disconnect();
      statsObserver.disconnect();
    };
  }, [hasAnimated]);

  const fetchDoctors = async () => {
    try {
      const res = await axios.get('/api/doctors.php');
      setDoctors(res.data);
    } catch (err) {
      console.error('Error fetching doctors:', err);
    }
  };

  const animateCount = (key, target, duration) => {
    const start = 0;
    const increment = target / (duration / 10);
    let current = start;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCounts(prev => ({ ...prev, [key]: target }));
        clearInterval(timer);
      } else {
        setCounts(prev => ({ ...prev, [key]: Math.floor(current) }));
      }
    }, 10);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedDoctor = doctors.find(d => d.id === parseInt(bookingData.doctorId));
      await axios.post('/api/online-appointments.php', {
        patientName: `${bookingData.firstName} ${bookingData.lastName}`,
        email: 'patient@example.com',
        phone: bookingData.mobile,
        age: bookingData.age,
        gender: bookingData.gender,
        bloodGroup: bookingData.bloodGroup,
        doctorName: selectedDoctor ? selectedDoctor.name : 'Any Available',
        message: `Date: ${bookingData.appointmentDate}, Time: ${bookingData.appointmentTime}, Address: ${bookingData.address}`
      });
      setShowBookingModal(false);
      setShowSuccessModal(true);
      setBookingData({
        firstName: '',
        lastName: '',
        mobile: '',
        age: '',
        gender: '',
        bloodGroup: '',
        appointmentDate: '',
        appointmentTime: '',
        address: '',
        doctorId: ''
      });
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('Failed to book appointment. Please try again.');
    }
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setBookingData({ ...bookingData, [name]: value });
    
    // Fetch booked times when doctor or date changes
    const doctorId = name === 'doctorId' ? value : bookingData.doctorId;
    const date = name === 'appointmentDate' ? value : bookingData.appointmentDate;
    
    if (doctorId && date) {
      try {
        console.log('Fetching booked slots for:', { doctorId, date });
        const res = await axios.get(`/api/online-appointments.php?action=booked-slots&doctorId=${doctorId}&date=${date}`);
        console.log('Booked slots response:', res.data);
        setBookedTimes(res.data.bookedTimes || []);
      } catch (err) {
        console.error('Error fetching booked slots:', err);
        setBookedTimes([]);
      }
    }
  };

  const handleHourChange = (e) => {
    const hour = e.target.value;
    console.log('Hour selected:', hour);
    console.log('Current booked times:', bookedTimes);
    setSelectedHour(hour);
    if (hour && selectedMinute) {
      const time = `${hour}:${selectedMinute}`;
      setBookingData({ ...bookingData, appointmentTime: time });
    }
  };

  const handleMinuteChange = (e) => {
    const minute = e.target.value;
    console.log('Minute selected:', minute);
    console.log('Is booked?', isTimeSlotBooked(selectedHour, minute));
    setSelectedMinute(minute);
    if (selectedHour && minute) {
      const time = `${selectedHour}:${minute}`;
      console.log('Final time:', time);
      setBookingData({ ...bookingData, appointmentTime: time });
    }
  };

  const isTimeSlotBooked = (hour, minute) => {
    if (!hour || !minute) return false;
    const time = `${hour}:${minute}`;
    const isBooked = bookedTimes.includes(time);
    console.log(`Checking ${time}:`, isBooked, 'Booked times:', bookedTimes);
    return isBooked;
  };

  const generateHours = () => {
    const hours = [];
    for (let h = 9; h <= 20; h++) {
      hours.push(h.toString().padStart(2, '0'));
    }
    return hours;
  };

  const generateMinutes = () => {
    const minutes = [];
    for (let m = 0; m < 60; m += 5) {
      minutes.push(m.toString().padStart(2, '0'));
    }
    return minutes;
  };

  return (
    <div className="landing-page">
      <nav className="medical-navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <i className="fas fa-heartbeat"></i>
            <span>MEDX ARENA</span>
          </div>
          <div className="nav-menu">
            <a href="#home">Home</a>
            <a href="#about">About Us</a>
            <a href="#services">Services</a>
            <a href="#doctors">Our Doctors</a>
            <a href="#pricing">Fees</a>
            <a href="#contact">Contact</a>
            <button className="nav-login-btn" onClick={onPatientLoginClick}>
              <i className="fas fa-user"></i> Patient Login
            </button>
            <button className="nav-login-btn" onClick={onStaffLoginClick}>
              <i className="fas fa-user-tie"></i> Staff Login
            </button>
          </div>
        </div>
      </nav>

      <section id="home" className="hero-section">
        <div className="hero-container">
          <div className="hero-left">
            <div className="hero-badge">
              <i className="fas fa-award"></i> Trusted Healthcare Provider
            </div>
            <h1 className="hero-main-title">Welcome to MEDX ARENA</h1>
            <p className="hero-description">
              Your trusted healthcare partner providing comprehensive medical services including 
              gynecology, obstetrics, general medicine, and advanced diagnostic facilities.
            </p>
            <div className="hero-cta">
              <button className="btn-get-started" onClick={() => {
                setShowBookingModal(true);
                setBookedTimes([]);
                setSelectedHour('');
                setSelectedMinute('');
              }}>
                <i className="fas fa-calendar-check"></i> Book Appointment
              </button>
              <button className="btn-watch-demo">
                <i className="fas fa-phone"></i> Call: +92-304-8064156
              </button>
            </div>
            <div className="hero-stats" ref={statsRef}>
              <div className="stat-item">
                <h3>{counts.years}+</h3>
                <p>Years Experience</p>
              </div>
              <div className="stat-item">
                <h3>{counts.patients >= 1000 ? `${(counts.patients / 1000).toFixed(0)}K+` : `${counts.patients}+`}</h3>
                <p>Happy Patients</p>
              </div>
              <div className="stat-item">
                <h3>{counts.emergency}/7</h3>
                <p>Emergency Care</p>
              </div>
            </div>
          </div>
          <div className="hero-right">
            <div className="hero-image-wrapper">
              <img src="/uploads/Hospital image.png" alt="Hospital" style={{ width: '100%', maxWidth: '700px', height: '600px', objectFit: 'cover', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} />
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="features-section">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <span className="section-badge">About Us</span>
            <h2>Why Choose MEDX ARENA?</h2>
            <p>Modern healthcare facility with experienced doctors and advanced equipment</p>
          </div>
          <div className="features-grid-advanced">
            <div className="feature-box animate-on-scroll">
              <div className="feature-icon-wrapper">
                <i className="fas fa-user-md"></i>
              </div>
              <h3>Expert Doctors</h3>
              <p>Highly qualified and experienced medical professionals dedicated to your health</p>
            </div>
            <div className="feature-box animate-on-scroll">
              <div className="feature-icon-wrapper">
                <i className="fas fa-hospital"></i>
              </div>
              <h3>Modern Facility</h3>
              <p>State-of-the-art medical equipment and comfortable patient care environment</p>
            </div>
            <div className="feature-box animate-on-scroll">
              <div className="feature-icon-wrapper">
                <i className="fas fa-ambulance"></i>
              </div>
              <h3>Emergency Services</h3>
              <p>24/7 emergency medical care with quick response and expert treatment</p>
            </div>
            <div className="feature-box animate-on-scroll">
              <div className="feature-icon-wrapper">
                <i className="fas fa-microscope"></i>
              </div>
              <h3>Advanced Lab</h3>
              <p>In-house laboratory with latest diagnostic equipment and accurate results</p>
            </div>
            <div className="feature-box animate-on-scroll">
              <div className="feature-icon-wrapper">
                <i className="fas fa-x-ray"></i>
              </div>
              <h3>Ultrasound & Imaging</h3>
              <p>Advanced ultrasound and imaging services including 3D/4D OBS scans</p>
            </div>
            <div className="feature-box animate-on-scroll">
              <div className="feature-icon-wrapper">
                <i className="fas fa-hand-holding-medical"></i>
              </div>
              <h3>Affordable Care</h3>
              <p>Quality healthcare at reasonable prices with flexible payment options</p>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="services-section">
        <div className="section-container">
          <div className="services-content">
            <div className="services-left animate-on-scroll">
              <span className="section-badge">Our Services</span>
              <h2>Comprehensive Medical Care</h2>
              <div className="service-list">
                <div className="service-item">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <h4>Gynecology & Obstetrics</h4>
                    <p>Complete women's health care and pregnancy management</p>
                  </div>
                </div>
                <div className="service-item">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <h4>General Medicine</h4>
                    <p>Treatment for common illnesses and health checkups</p>
                  </div>
                </div>
                <div className="service-item">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <h4>Laboratory Services</h4>
                    <p>Complete blood tests, urine tests, and diagnostic services</p>
                  </div>
                </div>
                <div className="service-item">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <h4>Vaccination Programs</h4>
                    <p>Child and adult vaccination with proper scheduling</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="services-right">
              <div className="service-card animate-on-scroll">
                <i className="fas fa-clock"></i>
                <h3>Flexible Timings</h3>
                <p>Open 7 days a week with extended hours for your convenience</p>
              </div>
              <div className="service-card animate-on-scroll">
                <i className="fas fa-credit-card"></i>
                <h3>Easy Payment</h3>
                <p>Cash, card, and insurance accepted with transparent billing</p>
              </div>
              <div className="service-card animate-on-scroll">
                <i className="fas fa-phone-volume"></i>
                <h3>Online Booking</h3>
                <p>Book appointments online or call us for immediate assistance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="doctors" className="testimonials-section">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <span className="section-badge">Our Doctors</span>
            <h2>Meet Our Expert Team</h2>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="author-avatar">
                <i className="fas fa-user-md"></i>
              </div>
              <h4>Dr. Ahmed Khan</h4>
              <span className="doctor-specialty">MBBS, FCPS (Gynecology)</span>
              <p>15+ years experience in women's health and obstetrics. Specialized in high-risk pregnancies.</p>
            </div>
            <div className="testimonial-card">
              <div className="author-avatar">
                <i className="fas fa-user-md"></i>
              </div>
              <h4>Dr. Sarah Ali</h4>
              <span className="doctor-specialty">MBBS, MD (Medicine)</span>
              <p>Expert in general medicine and preventive healthcare with 10+ years experience.</p>
            </div>
            <div className="testimonial-card">
              <div className="author-avatar">
                <i className="fas fa-user-md"></i>
              </div>
              <h4>Dr. Hassan Raza</h4>
              <span className="doctor-specialty">MBBS, FCPS (Radiology)</span>
              <p>Specialist in ultrasound and diagnostic imaging with advanced training.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonials-section" style={{background: '#f9fafb'}}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Patient Reviews</span>
            <h2>What Our Patients Say</h2>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
              <p>"Excellent care and very professional staff. Dr. Ahmed is very experienced and caring."</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <div>
                  <h4>Ayesha Khan</h4>
                  <span>Patient</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
              <p>"Best clinic in the area. Clean facility and friendly staff. Highly recommended!"</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <div>
                  <h4>Ali Raza</h4>
                  <span>Patient</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
              <p>"Very satisfied with the treatment. Affordable prices and quality care."</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <div>
                  <h4>Fatima Ahmed</h4>
                  <span>Patient</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Services & Fees</span>
            <h2>Our Medical Services</h2>
            <p>Affordable healthcare services for everyone</p>
          </div>
          
          <div className="pricing-category">
            <h3 className="category-title"><i className="fas fa-user-md"></i> Doctor Consultation Fees</h3>
            <div className="pricing-grid">
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-stethoscope"></i>
                </div>
                <h3>General Physician</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">1,000</span>
                  <span className="period">/visit</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> General checkup</li>
                  <li><i className="fas fa-check"></i> Prescription</li>
                  <li><i className="fas fa-check"></i> Follow-up advice</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
              
              <div className="pricing-card featured">
                <div className="popular-badge">Most Booked</div>
                <div className="service-icon">
                  <i className="fas fa-baby"></i>
                </div>
                <h3>Gynecologist / OBS</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">2,000</span>
                  <span className="period">/visit</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Consultation</li>
                  <li><i className="fas fa-check"></i> Ultrasound report</li>
                  <li><i className="fas fa-check"></i> Pregnancy care</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
              
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-heartbeat"></i>
                </div>
                <h3>Specialist Doctor</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">2,500</span>
                  <span className="period">/visit</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Expert consultation</li>
                  <li><i className="fas fa-check"></i> Detailed examination</li>
                  <li><i className="fas fa-check"></i> Treatment plan</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
            </div>
          </div>

          <div className="pricing-category">
            <h3 className="category-title"><i className="fas fa-vial"></i> Laboratory Tests</h3>
            <div className="pricing-grid">
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-microscope"></i>
                </div>
                <h3>Blood Tests</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">500</span>
                  <span className="period">- 3,000</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> CBC - Rs 500</li>
                  <li><i className="fas fa-check"></i> Blood Sugar - Rs 300</li>
                  <li><i className="fas fa-check"></i> Lipid Profile - Rs 1,200</li>
                  <li><i className="fas fa-check"></i> Complete Panel - Rs 3,000</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
              
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-x-ray"></i>
                </div>
                <h3>Ultrasound / Imaging</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">1,500</span>
                  <span className="period">- 5,000</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Ultrasound - Rs 1,500</li>
                  <li><i className="fas fa-check"></i> OBS Scan - Rs 2,000</li>
                  <li><i className="fas fa-check"></i> 3D/4D Scan - Rs 3,500</li>
                  <li><i className="fas fa-check"></i> X-Ray - Rs 800</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
              
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-flask"></i>
                </div>
                <h3>Urine & Other Tests</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">300</span>
                  <span className="period">- 2,000</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Urine Test - Rs 300</li>
                  <li><i className="fas fa-check"></i> Stool Test - Rs 400</li>
                  <li><i className="fas fa-check"></i> Thyroid Test - Rs 1,500</li>
                  <li><i className="fas fa-check"></i> Liver Function - Rs 1,200</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
            </div>
          </div>

          <div className="pricing-category">
            <h3 className="category-title"><i className="fas fa-hospital"></i> Clinic Services</h3>
            <div className="pricing-grid">
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-syringe"></i>
                </div>
                <h3>Vaccination</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">500</span>
                  <span className="period">- 2,000</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Child vaccination</li>
                  <li><i className="fas fa-check"></i> Adult vaccination</li>
                  <li><i className="fas fa-check"></i> Flu shots</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
              
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-procedures"></i>
                </div>
                <h3>Minor Procedures</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">1,000</span>
                  <span className="period">- 5,000</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Wound dressing</li>
                  <li><i className="fas fa-check"></i> Stitches</li>
                  <li><i className="fas fa-check"></i> Minor surgery</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Book Now</button>
              </div>
              
              <div className="pricing-card">
                <div className="service-icon">
                  <i className="fas fa-file-medical-alt"></i>
                </div>
                <h3>Medical Reports</h3>
                <div className="price">
                  <span className="currency">Rs</span>
                  <span className="amount">500</span>
                  <span className="period">- 1,500</span>
                </div>
                <ul className="pricing-features">
                  <li><i className="fas fa-check"></i> Medical certificate</li>
                  <li><i className="fas fa-check"></i> Fitness certificate</li>
                  <li><i className="fas fa-check"></i> Report copies</li>
                </ul>
                <button className="pricing-btn" onClick={onPatientLoginClick}>Request Now</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-content">
            <div className="contact-left">
              <span className="section-badge">Contact Us</span>
              <h2>Visit Our Clinic</h2>
              <p>We're here to help you with your healthcare needs</p>
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <i className="fas fa-phone"></i>
                  <div>
                    <h4>Phone</h4>
                    <p>+92-304-8064156</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <h4>Email</h4>
                    <p>medxarena@gmail.com</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <h4>Location</h4>
                    <p>Pakistan</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-right">
              <form className="contact-form">
                <input type="text" placeholder="Your Name" required />
                <input type="email" placeholder="Your Email" required />
                <input type="tel" placeholder="Phone Number" required />
                <textarea placeholder="Your Message" rows="5" required></textarea>
                <button type="submit" className="contact-submit-btn">
                  <i className="fas fa-paper-plane"></i> Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer className="medical-footer">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-col">
              <div className="footer-logo">
                <i className="fas fa-heartbeat"></i>
                <span>MEDX ARENA</span>
              </div>
              <p>Your trusted healthcare partner providing quality medical services with care and compassion.</p>
              <div className="footer-social">
                <a href="https://www.facebook.com/share/1C6MJAqfUT/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer"><i className="fab fa-facebook"></i></a>
                <a href="#"><i className="fab fa-twitter"></i></a>
                <a href="#"><i className="fab fa-linkedin"></i></a>
                <a href="https://www.instagram.com/medxarena?igsh=MWs0d25kaHZmZzdydg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
              </div>
            </div>
            <div className="footer-col">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#about">About Us</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#doctors">Our Doctors</a></li>
                <li><a href="#pricing">Fees</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Clinic Hours</h4>
              <ul>
                <li>Mon - Sat: 9:00 AM - 9:00 PM</li>
                <li>Sunday: 10:00 AM - 6:00 PM</li>
                <li>Emergency: 24/7</li>
                <li><a href="#contact">Contact Us</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Newsletter</h4>
              <p>Subscribe for health tips and updates</p>
              <div className="newsletter-form">
                <input type="email" placeholder="Your email" />
                <button><i className="fas fa-arrow-right"></i></button>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 MEDX ARENA. All rights reserved. | Developer: Abdullah Arif | +92-3137323866</p>
          </div>
        </div>
      </footer>

      {showBookingModal && (
        <div className="modal-overlay" onClick={() => {
          setShowBookingModal(false);
          setBookedTimes([]);
          setSelectedHour('');
          setSelectedMinute('');
        }} style={{animation: 'fadeIn 0.15s'}}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{animation: 'slideUp 0.15s'}}>
            <div className="modal-header">
              <div className="modal-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <h2>Book Your Appointment</h2>
              <p>Fill in your details and we'll confirm your appointment</p>
            </div>
            <form onSubmit={handleBookingSubmit} className="booking-form">
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-user"></i> First Name</label>
                  <input type="text" name="firstName" value={bookingData.firstName} onChange={handleInputChange} placeholder="Enter first name" required />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-user"></i> Last Name</label>
                  <input type="text" name="lastName" value={bookingData.lastName} onChange={handleInputChange} placeholder="Enter last name" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-phone"></i> Mobile Number</label>
                  <input type="tel" name="mobile" value={bookingData.mobile} onChange={handleInputChange} placeholder="03XX-XXXXXXX" required />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-birthday-cake"></i> Age</label>
                  <input type="number" name="age" value={bookingData.age} onChange={handleInputChange} placeholder="Enter age" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-venus-mars"></i> Gender</label>
                  <select name="gender" value={bookingData.gender} onChange={handleInputChange} required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label><i className="fas fa-tint"></i> Blood Group</label>
                  <select name="bloodGroup" value={bookingData.bloodGroup} onChange={handleInputChange}>
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-user-md"></i> Select Doctor</label>
                  <select name="doctorId" value={bookingData.doctorId} onChange={handleInputChange} required>
                    <option value="">Select Doctor</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>Dr. {doctor.name} - {doctor.specialization}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label><i className="fas fa-calendar-alt"></i> Appointment Date</label>
                  <input 
                    type="date" 
                    name="appointmentDate" 
                    value={bookingData.appointmentDate} 
                    onChange={handleInputChange} 
                    min={new Date().toISOString().split('T')[0]}
                    required 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-clock"></i> Hour</label>
                  <select 
                    value={selectedHour} 
                    onChange={handleHourChange}
                    required
                    disabled={!bookingData.doctorId || !bookingData.appointmentDate}
                  >
                    <option value="">Select Hour</option>
                    {generateHours().map(hour => (
                      <option key={hour} value={hour}>{hour}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label><i className="fas fa-clock"></i> Minute</label>
                  <select 
                    value={selectedMinute} 
                    onChange={handleMinuteChange}
                    required
                    disabled={!bookingData.doctorId || !bookingData.appointmentDate || !selectedHour}
                  >
                    <option value="">Select Minute</option>
                    {generateMinutes().map(minute => {
                      const isBooked = isTimeSlotBooked(selectedHour, minute);
                      return (
                        <option 
                          key={minute} 
                          value={minute}
                          disabled={isBooked}
                          style={isBooked ? {color: '#999', fontStyle: 'italic'} : {}}
                        >
                          {minute} {isBooked ? '(Booked)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {!bookingData.doctorId || !bookingData.appointmentDate ? (
                    <small style={{color: '#6b7280', marginTop: '5px', display: 'block'}}>Please select doctor and date first</small>
                  ) : !selectedHour ? (
                    <small style={{color: '#6b7280', marginTop: '5px', display: 'block'}}>Please select hour first</small>
                  ) : null}
                </div>
              </div>
              <div className="form-group full-width">
                <label><i className="fas fa-map-marker-alt"></i> Address</label>
                <textarea name="address" value={bookingData.address} onChange={handleInputChange} rows="3" placeholder="Enter your complete address" required></textarea>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button type="button" className="cancel-booking-btn" onClick={() => {
                  setShowBookingModal(false);
                  setBookedTimes([]);
                  setSelectedHour('');
                  setSelectedMinute('');
                }}>
                  <i className="fas fa-times-circle"></i> Cancel
                </button>
                <button type="submit" className="submit-booking-btn">
                  <i className="fas fa-check-circle"></i> Confirm Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay" style={{animation: 'fadeIn 0.2s', zIndex: 10000}}>
          <div className="success-modal" style={{animation: 'scaleIn 0.3s'}}>
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="38" fill="#10b981" stroke="#fff" strokeWidth="3"/>
                <path d="M25 40 L35 50 L55 30" stroke="#fff" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={{color: '#1f2937', marginTop: '20px', fontSize: '1.8rem'}}>Appointment Booked!</h2>
            <p style={{color: '#6b7280', fontSize: '1.1rem'}}>We will contact you soon</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
